/**
 * Llamadas directas a la API REST de Strapi (sin @nuxtjs/strapi).
 * Compatible con Strapi v4/v5.
 */

import { getErrorMessage } from './errors'

interface StrapiQuery {
  locale?: string
  populate?: string[] | string
}

const getStrapiBaseUrl = (): string => {
  const config = useRuntimeConfig()
  if (import.meta.server) {
    return (config.public.STRAPI_SERVER_URL as string) || ''
  }
  return (config.public.STRAPI_CLIENT_URL as string) || ''
}

/**
 * Construye query string para Strapi (locale + populate[0]=x&populate[1]=y).
 */
const buildStrapiQueryString = (query: StrapiQuery): string => {
  const params = new URLSearchParams()
  if (query.locale) {
    params.set('locale', query.locale)
  }
  const populate = query.populate
  if (populate !== undefined) {
    const arr = Array.isArray(populate) ? populate : [populate]
    arr.forEach((value, i) => params.set(`populate[${i}]`, String(value)))
  }
  return params.toString()
}

/**
 * Obtiene contenido de Strapi por tipo (single type o collection).
*/
function unwrapStrapiPayload(response: unknown): unknown {
  if (response !== null && typeof response === 'object' && 'data' in response) {
    return (response as { data?: unknown }).data ?? response
  }
  return response
}

export const getStrapiData = async (url: string, query: StrapiQuery): Promise<unknown> => {
  const localeValue =
    query.locale !== undefined && query.locale !== null
      ? query.locale
      : useI18n().locale.value
  let content: unknown

  try {
    const baseUrl = getStrapiBaseUrl()
    if (!baseUrl) {
      throw new Error('Strapi URL not configured')
    }

    const fullQuery = { ...query, locale: localeValue }
    const queryString = buildStrapiQueryString(fullQuery)
    const apiUrl = `${baseUrl.replace(/\/$/, '')}/api/${url}${queryString ? `?${queryString}` : ''}`

    const response = await $fetch(apiUrl)

    // Strapi REST: single type { data: {...} }, collection { data: [...] }
    content = unwrapStrapiPayload(response)
  } catch (error: unknown) {
    console.error('Error llamada Strapi', getErrorMessage(error))
    try {
      const contentData = await $fetch(`/strapi/${localeValue}/${url}`)
      content = extractStrapiData(Array.isArray(contentData) ? contentData[0] : contentData)
    } catch (fetchError: unknown) {
      console.error('Error llamada de respaldo', getErrorMessage(fetchError))
    }
  }

  return content
}

export const extractStrapiData = (response: unknown): unknown => {
  if (response !== null && typeof response === 'object' && 'data' in response) {
    return (response as { data?: unknown }).data
  }
  return undefined
}

/**
 * Obtiene un documento de Strapi por filtro (ej. identifier, slug).
 */
export const getStrapiDataBy = async (
  url: string,
  by: string,
  queryValue: string,
  locale: string
): Promise<unknown> => {
  const config = useRuntimeConfig()
  const baseUrl = import.meta.server
    ? (config.public.STRAPI_SERVER_URL as string)
    : (config.public.STRAPI_CLIENT_URL as string)

  let content: unknown
  try {
    if (baseUrl) {
      const apiUrl = `${baseUrl.replace(/\/$/, '')}/api/${url}`
      const response = await $fetch(apiUrl, {
        query: {
          [`filters[${by}]`]: queryValue,
          locale
        }
      })
      const d = (response as { data?: unknown }).data
      content = (Array.isArray(d) ? d[0] : undefined) ?? d
    } else {
      throw new Error('Strapi URL not configured')
    }
  } catch (error: unknown) {
    console.error('Error llamada Strapi', getErrorMessage(error))
    try {
      const contentData = await $fetch(`/strapi/${locale}/${url}`)
      const raw = Array.isArray(contentData) ? contentData[0] : contentData
      content = extractStrapiData(raw)
    } catch (fetchError: unknown) {
      console.error('Error llamada de respaldo', getErrorMessage(fetchError))
      throw new Error('No se pudo obtener los datos de Strapi')
    }
  }
  return content
}

export const getImageSrc = (strapiData: (StrapiData | StrapiImageRef) | null | undefined, path: string): string => {
  if (!strapiData) return ''
  const provider = strapiData?.provider
  const image = strapiData
  if (provider === 's3Provider') {
    const fileName = 'name' in image && image.name ? image.name : ''
    return `${path}/${fileName}`
  }
  return image?.url ?? ''
}